import { adminGet } from './client';

export interface AdminMe { email: string; isAdmin: true }

export interface EngagementTotals {
  visitorSessions: number;
  totalActiveSeconds: number;
  averageActiveSeconds: number;
}

export interface EngagementWebsiteTotals extends EngagementTotals {
  /** Visitors clearing the GA4-style bar. Reported beside the raw count, not instead. */
  engagedVisitors: number;
}

export interface Overview {
  generatedAt: string;
  freshness: { mode: 'live'; maxAgeSeconds: number };
  version: { gitSha: string; pipelineVersion: string; builtAt: string };
  presence: {
    generatedAt: string; browserSessions: number; authenticatedSessions: number; authenticatedUsers: number; anonymousSessions: number;
    firstSeen: { last5Minutes: number; last15Minutes: number; last60Minutes: number };
    peakBrowserSessionsSinceRestart: number; routeGroups: Record<string, number>;
  };
  engagement: {
    status: 'up' | 'degraded'; day: string; timeZone: string; retentionDays: number;
    engagedSecondsThreshold: number;
    website: EngagementWebsiteTotals;
    puzzles: EngagementTotals;
    /** Oldest first, today last. Quiet days are present with zeros. */
    history: Array<{ day: string; website: EngagementWebsiteTotals; puzzles: EngagementTotals }>;
  };
  measurement: {
    status: 'ok' | 'missing' | 'unreachable';
    measurementId: string | null;
    bundle: string | null;
    checkedAt: string | null;
    detail: string | null;
  };
  onlinePlay: {
    websocketConnections: number; activeGames: number;
    matchmaking: { seekers: number; parked: number; reserved: number; buckets: Record<string, number> };
  };
  productActivity: {
    day: string; timeZone: string;
    /* C6 — provenance and honest nullability. `null` means the source could not
       be read; `0` means it was read and nothing happened. The nine counters
       below used to be zero-filled on failure, so the two were the same number. */
    status: 'ok' | 'degraded'; windowStart: string | null; asOfExclusive: string | null;
    puzzlesServed: number | null; puzzleRequests: number | null; botHeartbeatsIgnored: number | null;
    puzzlesSolved: number | null; puzzlesFailed: number | null;
    websocketConnections: number | null; anonymousWebsocketConnections: number | null;
    gamesStarted: number | null; analysisRequests: number | null;
    trainingPlansCreated: number | null; trainingPlansToday: number | null;
    trainingPlansWithActivity: number | null;
    accountsCreated: number | null; accountsCreatedToday: number | null;
    /* C4 — Cognito's own observation time and status. It is paginated and not
       transactionally consistent with SQLite, so it never inherits the window's
       watermark and must not be rendered as though it did. */
    accountsObservedAt: string | null; accountsStatus: 'ok' | 'degraded' | 'unavailable';
    puzzleRunsToday: number | null; puzzlesAttemptedToday: number | null; puzzlesAttemptedPerSolverToday: number | null;
    puzzleReporting: {
      status: 'ok'; day: string; timeZone: string; windowStart: string; asOfExclusive: string;
      inclusionPolicy: 'customer_and_unknown_legacy'; served: number;
      /* B5 — serving derived from presentation rows. One request can return
         several positions, so the batch is stated rather than inferred. */
      serving: {
        requests: number; positions: number;
        positionsPerRequest: { mean: number | null; median: number | null; p90: number | null; maximum: number | null };
        byEndpoint: { next: number; daily: number; by_id: number };
      };
      /* The legacy counters, carried and explicitly NOT comparable: they were
         incremented by /next only, carry no source and have no watermark. */
      shadowCounters: {
        puzzlesServed: number | null; puzzleRequests: number | null;
        comparable: false; incomparableBecause: string[];
      };
      eventFlow: { started: number; results: number; solved: number; failed: number };
      startCohort: { started: number; resolvedByWatermark: number; unresolvedAtWatermark: number };
      scoringIdentities: number; authenticatedUsers: number; anonymousBrowserIdentities: number;
      scoredResultsPerIdentity: { mean: number | null; median: number | null; p90: number | null; maximum: number | null; topSolverShare: number | null };
      excludedInternal: number; legacyUnkeyed: number;
    } | { status: 'unavailable'; detail: string };
    /* B7 — two window TOTALS and one GAUGE, kept apart so the page cannot add
       them together. `startedAtUnknown` is the pre-migration-026 rows. */
    onlineGameReporting:
      | { started: number; completed: number; activeNow: number; startedAtUnknown: number }
      | { status: 'unavailable'; detail: string };
    /* B6 — `requests` is LOGICAL work deduplicated by request id;
       `repeatRequests` is how often the same one was asked for again. */
    analysisReporting:
      | { requests: number; completed: number; failed: number; stillRunning: number; repeatRequests: number }
      | { status: 'unavailable'; detail: string };
    /* C3 — runs and plans by their own timestamps. `started` and `completed`
       are NOT meant to reconcile: a run can begin one day and end the next.
       `activeNow` is a gauge; `allTime` is growth, not a daily figure. */
    trainingReporting:
      | {
        runs: { started: number; completed: number; abandoned: number; activeNow: number };
        plans: { createdInWindow: number; activeNow: number; allTime: number };
      }
      | { status: 'unavailable'; detail: string };
    /* The SAME figures as the tiles above, per day, from the same tables through
       the same predicates (2026-08-23). It used to count four unrelated tables
       with no activity_source filter, so it included our own probe traffic while
       the tiles excluded it. Starts at the first day the contract can describe. */
    history: Array<{
      day: string; served: number; started: number;
      results: number; solved: number; failed: number; people: number;
    }> | null;
    /* A gauge with its own observation time (C5), not a total over the window. */
    explanationQueue: { observedAt: string; enabled: boolean; queued: number; inFlight: number; baked: number; hasError: boolean };
  };
  traffic: { topUserAgents: Array<{ agent: string; count: number; isBot: boolean; reason: string | null }> };
  system: {
    processUptimeSeconds: number; nodeVersion: string; memoryBytes: { heapUsed: number; heapTotal: number; rss: number };
    dependencies: { database: { status: string; responseTimeMs?: number }; cache: { status: string; layers: Record<string, unknown> } };
  };
}

export const getAdminMe = (signal?: AbortSignal) => adminGet<AdminMe>('/api/admin/me', signal);
export const getOverview = (signal?: AbortSignal) => adminGet<Overview>('/api/admin/overview', signal);
